/**
 * Controls — the DOM chrome: top bar (logo, language, how-to, balance) and
 * the bottom control bar (bet, difficulty, Play/GO/CASH OUT), plus toasts and
 * the how-to-play modal. Localized (Arabic default, English toggle).
 */
import { formatMoney } from '../game/Economy.js';
import { formatMultiplier } from '../game/Difficulty.js';
import { DIFFICULTIES } from '../game/Difficulty.js';
import { t, getLang, setLang } from './i18n.js';

const CHIPS = [0.5, 1, 2, 7, 25, 100];

export default class Controls {
  constructor({ bus, game, economy, coinIcon }) {
    this.bus = bus;
    this.game = game;
    this.economy = economy;
    this.t = t();

    document.documentElement.lang = getLang();
    document.documentElement.dir = this.t.dir;

    this.top = document.getElementById('topbar');
    this.bar = document.getElementById('controls');
    this.overlay = document.getElementById('overlay');
    this._buildTop(coinIcon);
    this._buildBar();
    this._buildModal();
    this._wire();
    this._render();
  }

  /* ---------------- DOM scaffolding ---------------- */

  _buildTop(coinIcon) {
    const T = this.t;
    this.top.innerHTML = `
      <div class="logo">CHICKEN<span>ROAD</span></div>
      <div class="top-right">
        <button class="lang-btn" id="lang-btn">${T.toggleLabel}</button>
        <button class="howto" id="howto-btn">${T.howto}</button>
        <div class="balance">
          <img class="coin-ico" alt="" src="${coinIcon}" />
          <span id="balance-amt">0.00</span><span class="cur">$</span>
        </div>
      </div>`;
  }

  _buildBar() {
    const T = this.t;
    this.bar.innerHTML = `
      <div class="bet-block">
        <div class="block-label">${T.bet}</div>
        <div class="bet-row">
          <button class="mini" id="bet-min">MIN</button>
          <input id="bet-input" type="text" inputmode="decimal" autocomplete="off" />
          <button class="mini" id="bet-max">MAX</button>
        </div>
        <div class="chips">
          ${CHIPS.map((v) => `<button class="chip" data-v="${v}">${v}</button>`).join('')}
        </div>
      </div>
      <div class="diff-block">
        <div class="block-label">${T.difficulty}</div>
        <div class="segment" id="diff-seg">
          ${DIFFICULTIES.map((d) => `<button data-id="${d.id}" class="${d.id === 'easy' ? 'on' : ''}">${T.diffNames[d.id]}</button>`).join('')}
        </div>
        <div class="odds" id="odds-line"></div>
      </div>
      <div class="action-block">
        <button id="cashout-btn" class="cashout" hidden>
          <span class="co-label">${T.cashout}</span>
          <span class="co-amt" id="cashout-amt">$0.00</span>
        </button>
        <button id="play-btn" class="play">${T.play}</button>
      </div>`;

    this.el = {
      balance: document.getElementById('balance-amt'),
      betInput: document.getElementById('bet-input'),
      diffSeg: document.getElementById('diff-seg'),
      odds: document.getElementById('odds-line'),
      play: document.getElementById('play-btn'),
      cashout: document.getElementById('cashout-btn'),
      cashoutAmt: document.getElementById('cashout-amt'),
    };
  }

  _buildModal() {
    const T = this.t;
    this.modal = document.createElement('div');
    this.modal.className = 'modal-back';
    this.modal.hidden = true;
    this.modal.innerHTML = `
      <div class="modal">
        <h2>${T.modalTitle}</h2>
        <ol>${T.modalSteps.map((s) => `<li>${s}</li>`).join('')}</ol>
        <p class="fair" id="fair-line"></p>
        <button class="modal-close" id="modal-close">${T.gotIt}</button>
      </div>`;
    this.overlay.appendChild(this.modal);
    this.toastHost = document.createElement('div');
    this.toastHost.className = 'toast-host';
    this.overlay.appendChild(this.toastHost);
  }

  /* ---------------- events ---------------- */

  _wire() {
    const { el } = this;
    const click = () => this.bus.emit('sfx:click', {});

    document.getElementById('lang-btn').addEventListener('click', () => {
      setLang(getLang() === 'ar' ? 'en' : 'ar');
      location.reload();
    });

    el.play.addEventListener('click', () => {
      click();
      if (this.game.state === 'idle') this.game.start();
      else if (this.game.state === 'waiting') this.game.go();
    });
    el.cashout.addEventListener('click', () => {
      click();
      this.game.cashOut();
    });

    // space = Play/GO, C = cash out (with debounce handled by the state machine)
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (this.game.state === 'idle') this.game.start();
        else if (this.game.state === 'waiting') this.game.go();
      } else if (e.key.toLowerCase() === 'c') {
        this.game.cashOut();
      }
    });

    el.betInput.addEventListener('change', () => {
      const v = parseFloat(el.betInput.value.replace(',', '.'));
      this.economy.setBet(Number.isFinite(v) ? v : this.economy.bet);
      this._render();
    });
    document.getElementById('bet-min').addEventListener('click', () => {
      click();
      this.economy.setBet(this.economy.minBet);
      this._render();
    });
    document.getElementById('bet-max').addEventListener('click', () => {
      click();
      this.economy.setBet(this.economy.maxBet);
      this._render();
    });
    this.bar.querySelectorAll('.chip').forEach((chip) =>
      chip.addEventListener('click', () => {
        click();
        this.economy.setBet(parseFloat(chip.dataset.v));
        this._render();
      })
    );

    el.diffSeg.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', () => {
        click();
        this.game.setDifficulty(btn.dataset.id);
      })
    );

    document.getElementById('howto-btn').addEventListener('click', async () => {
      click();
      const commit = await this.game.rng.commitment();
      document.getElementById('fair-line').textContent = this.t.fair(commit.slice(0, 20));
      this.modal.hidden = false;
    });
    document.getElementById('modal-close').addEventListener('click', () => (this.modal.hidden = true));
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.modal.hidden = true;
    });

    this.bus.on('game:state', () => this._render());
    this.bus.on('game:difficulty', () => this._render());
    this.bus.on('economy:balance', () => this._render());
    this.bus.on('economy:bet', () => this._render());
    this.bus.on('chicken:landed', ({ potential }) => {
      this.el.cashoutAmt.textContent = `$${formatMoney(potential)}`;
      this._flash(this.el.cashout);
    });
    this.bus.on('economy:rejected', () => this._shake(this.el.betInput.parentElement));
    this.bus.on('economy:topup', () => this._toast('lose', this.t.topup));

    this.bus.on('round:cashout', ({ multiplier, payout }) => {
      this._toast('win', '', (node) => {
        node.innerHTML = `<b class="toast-mult">${formatMultiplier(multiplier)}x</b><span class="toast-amt">+$0.00</span>`;
        const amt = node.querySelector('.toast-amt');
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / 750);
          amt.textContent = `+$${formatMoney(payout * (1 - Math.pow(1 - p, 3)))}`;
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    });
    this.bus.on('round:dead', ({ lost }) => {
      this._toast('lose', this.t.lost(formatMoney(lost)));
    });
  }

  /* ---------------- rendering ---------------- */

  _render() {
    const { el } = this;
    const T = this.t;
    const g = this.game;
    const idle = g.state === 'idle';
    const inRound = g.state === 'waiting' || g.state === 'hopping';

    el.balance.textContent = formatMoney(this.economy.balance);
    if (document.activeElement !== el.betInput) {
      el.betInput.value = formatMoney(this.economy.bet);
    }

    // bet + difficulty lock during a round
    this.bar.querySelectorAll('.bet-block button, .bet-block input, .diff-block button')
      .forEach((n) => (n.disabled = !idle));
    this.bar.classList.toggle('locked', !idle);

    el.diffSeg.querySelectorAll('button').forEach((b) =>
      b.classList.toggle('on', b.dataset.id === g.difficulty.id));
    el.odds.innerHTML = T.odds(g.difficulty.K);

    el.play.textContent = inRound ? T.go : T.play;
    el.play.disabled = g.state === 'starting' || g.state === 'hopping' || g.state === 'dead' || g.state === 'cashed';
    el.play.classList.toggle('go', inRound);

    const showCash = inRound && g.lane >= 1;
    el.cashout.hidden = !showCash;
    el.cashout.disabled = g.state !== 'waiting';
    if (showCash) el.cashoutAmt.textContent = `$${formatMoney(g.potential)}`;
  }

  _toast(kind, text, build) {
    const node = document.createElement('div');
    node.className = `toast ${kind}`;
    if (build) build(node);
    else node.textContent = text;
    this.toastHost.appendChild(node);
    requestAnimationFrame(() => node.classList.add('show'));
    setTimeout(() => {
      node.classList.remove('show');
      setTimeout(() => node.remove(), 350);
    }, 2100);
  }

  _shake(node) {
    node.classList.remove('shake');
    void node.offsetWidth;
    node.classList.add('shake');
  }

  _flash(node) {
    node.classList.remove('flash');
    void node.offsetWidth;
    node.classList.add('flash');
  }
}
